import React, { useEffect, useState } from "react";
import TopBar from "../../components/TopBar";
import EmptyState from "../../components/EmptyState";
import { api, fmtUSD, fmtNum } from "../../lib/api";
import { useYear } from "../../context/YearContext";
import { Users, TrendingUp, ArrowDown, ArrowUp } from "lucide-react";

const BUSINESS_LABEL = {
  jk_repair: "Repair",
  jk_trucking: "Trucking",
  farm: "Farm",
};

export default function CustomerProfitability({ businessId, title, subtitle }) {
  const { year } = useYear();
  const [data, setData] = useState({ rows: [], totals: {} });
  const [sort, setSort] = useState({ key: "revenue", dir: "desc" });

  const load = async () => {
    const url = `/customer-pnl?year=${year}${businessId ? `&business_id=${businessId}` : ""}`;
    const { data } = await api.get(url);
    setData(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, businessId]);

  const sorted = [...data.rows].sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "string") {
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return sort.dir === "asc" ? av - bv : bv - av;
  });

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  };

  const Header = ({ k, label, align = "left" }) => {
    const active = sort.key === k;
    return (
      <th className={align === "right" ? "num" : ""}>
        <button
          type="button"
          onClick={() => toggleSort(k)}
          data-testid={`pnl-sort-${k}`}
          className={`inline-flex items-center gap-1 hover:text-[#BC4727] ${active ? "text-[#1F1E1B]" : "text-[#66645E]"}`}
        >
          {label}
          {active && (sort.dir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
        </button>
      </th>
    );
  };

  const t = data.totals || {};

  return (
    <div>
      <TopBar
        title={title || "Customer Profitability"}
        subtitle={subtitle || `Who's making you the most money — by customer, in ${year}.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="customer-pnl-totals">
        <Stat label="Customers" value={t.customer_count || 0} />
        <Stat label="Invoices" value={t.invoice_count || 0} />
        <Stat label="Revenue" value={fmtUSD(t.revenue || 0)} tone="positive" />
        <Stat label="Open Balance" value={fmtUSD(t.open_balance || 0)} tone={t.open_balance > 0 ? "warning" : "muted"} />
        <Stat label="Profit (tracked)" value={fmtUSD(t.profit || 0)} tone={t.profit >= 0 ? "highlight" : "negative"} testid="customer-pnl-profit" />
      </div>

      {sorted.length === 0 ? (
        <EmptyState icon={Users} title="No invoiced customers yet" hint={`Once you invoice someone in ${year}, they show up here ranked by revenue.`} testid="empty-customer-pnl" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto" data-testid="customer-pnl-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F4F0] border-b-2 border-[#1F1E1B]">
              <tr>
                <Header k="customer_name" label="Customer" />
                <Header k="invoice_count" label="Inv" align="right" />
                <Header k="revenue" label="Revenue" align="right" />
                <Header k="avg_invoice" label="Avg / Inv" align="right" />
                <Header k="cost" label="Tracked Cost" align="right" />
                <Header k="profit" label="Profit" align="right" />
                <Header k="margin_pct" label="Margin %" align="right" />
                <Header k="open_balance" label="Open Bal" align="right" />
                <Header k="last_invoice" label="Last Invoice" align="right" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.customer_id} className="border-b border-[#E5E2D6] last:border-b-0" data-testid={`pnl-row-${r.customer_id}`}>
                  <td>
                    <div className="font-semibold">{r.customer_name}</div>
                    {r.company && <div className="text-[11px] text-[#66645E]">{r.company}</div>}
                    <div className="flex gap-1 mt-0.5">
                      {r.businesses.map((b) => (
                        <span key={b} className="text-[9px] bg-[#1F1E1B] text-white px-1 py-0.5 font-bold uppercase tracking-wider">{BUSINESS_LABEL[b] || b}</span>
                      ))}
                      {r.is_internal && <span className="text-[9px] bg-[#5C4A2B] text-white px-1 py-0.5 font-bold uppercase tracking-wider">Internal</span>}
                    </div>
                  </td>
                  <td className="num">
                    <div className="tabular">{r.invoice_count}</div>
                    {r.paid_count > 0 && <div className="text-[10px] text-[#1A4135]">{r.paid_count} paid</div>}
                  </td>
                  <td className="num font-bold tabular text-[#1A4135]">{fmtUSD(r.revenue)}</td>
                  <td className="num tabular text-[#66645E]">{fmtUSD(r.avg_invoice)}</td>
                  <td className="num tabular text-[#BC4727]">{r.cost > 0 ? fmtUSD(r.cost) : "—"}</td>
                  <td className={`num font-bold tabular ${r.profit >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                    {r.cost > 0 ? fmtUSD(r.profit) : <span className="text-[#66645E] font-normal">—</span>}
                  </td>
                  <td className="num tabular text-[#66645E]">{r.cost > 0 ? `${fmtNum(r.margin_pct, 1)}%` : "—"}</td>
                  <td className="num tabular">
                    {r.open_balance > 0
                      ? <span className="text-[#C77E1A] font-bold">{fmtUSD(r.open_balance)}</span>
                      : <span className="text-[#66645E]">—</span>}
                  </td>
                  <td className="num text-[#66645E] text-[11px]">{r.last_invoice || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bg-[#1F1E1B] text-white p-3 flex items-baseline justify-between" data-testid="customer-pnl-footer">
            <span className="text-[10px] font-bold uppercase tracking-wider">
              <TrendingUp className="w-3 h-3 inline mr-1" /> Profit shown is from invoice line cost snapshots (parts only). Labor/load lines don't track cost.
            </span>
            <span className="font-display text-xl font-black tabular">
              {fmtUSD(t.revenue || 0)} rev · {fmtUSD(t.profit || 0)} profit
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = tone === "positive" ? "text-[#1A4135]"
              : tone === "warning"  ? "text-[#C77E1A]"
              : tone === "highlight" ? "text-[#1A4135]"
              : tone === "negative" ? "text-[#BC4727]"
              : tone === "muted" ? "text-[#66645E]"
              : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-4" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-2xl font-black tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}
